import { Component, EventListener, GondelBaseComponent } from '@gondel/core';

/**
 * nav-in-page implementation.
 * @author David Damnjanovic <david.damnjanovic@merkle.com>
 */

enum Selectors {
	AnchorTrigger = '[data-inpagenav="true"]',
	InPageNav = '.js-m-nav-in-page__anchors',
	ScrollableHelper = '.m-nav-in-page__scrollable-shadow-helper',
	ArrowLeftBtn = '.m-nav-in-page-arrow--left',
	ArrowRightBtn = '.m-nav-in-page-arrow--right',
}

enum Classes {
	ScrollShadowLeft = 'm-nav-in-page__scrollable-shadow-left',
	ScrollShadowRight = 'm-nav-in-page__scrollable-shadow-right',
}
enum States {
	AnchorActive = 'state-m-nav-in-page-anchor--active',
	InPageNavVisible = 'state-m-nav-in-page__anchors--visible',
}

@Component('NavInPage')
export class NavInPage extends GondelBaseComponent {
	private _allAnchorTriggers: NodeListOf<HTMLElement> = {} as NodeListOf<HTMLElement>;
	private _inPageNav: HTMLDivElement = {} as HTMLDivElement;
	private _updateNavShadows: any;
	private _updateInPageNav: any;
	private _isAnchorVisible: any;
	private _handleNavTopUsingDrupalToolbarsHeight: any;
	private _scrollableHelper: Element = {} as Element;

	public start() {
		this._inPageNav = this._ctx.querySelector(Selectors.InPageNav) as HTMLDivElement;
		this._allAnchorTriggers = document.querySelectorAll(Selectors.AnchorTrigger);
		this._scrollableHelper = this._ctx.querySelector(Selectors.ScrollableHelper) as Element;

		this._updateInPageNav = () => {
			const scrollY = window.scrollY;
			this._allAnchorTriggers.forEach((el, i) => {
				const trackingSection = el.closest('.cms-anchor')?.nextElementSibling as HTMLElement;
				const trackingSectionnHeight = trackingSection?.offsetHeight;
				const trackingSectionTop = trackingSection?.offsetTop - window.innerHeight / 1.5;
				const relatedAnchor = this._ctx.querySelector(`[href="#${el.id}"]`);
				if (
					scrollY > trackingSectionTop &&
					scrollY <= trackingSection?.offsetTop - 300 + trackingSectionnHeight
				) {
					relatedAnchor?.classList.add(States.AnchorActive);
					if (!this._isAnchorVisible(relatedAnchor)) {
						setTimeout(() => {
							this._inPageNav?.scroll({
								left: this._getScrollStep() * i,
								behavior: 'smooth',
							});
						}, 500);
					}
				} else {
					relatedAnchor?.classList.remove(States.AnchorActive);
				}
			});
		};

		this._isAnchorVisible = (el) => {
			const { left, right } = el.getBoundingClientRect();
			const wrapperRect = this._inPageNav.getBoundingClientRect();
			return !(left - 20 <= wrapperRect.left || Number(right) + 20 >= wrapperRect.right);
		};

		this._updateNavShadows = () => {
			const scrollLeft = this._inPageNav.scrollLeft;
			const maxScrollLeft = this._inPageNav.scrollWidth - this._inPageNav.clientWidth;
			if (this._inPageNav.scrollWidth <= this._inPageNav.clientWidth) {
				this._scrollableHelper?.classList.remove(Classes.ScrollShadowLeft, Classes.ScrollShadowRight);
			} else {
				this._scrollableHelper?.classList.toggle(
					Classes.ScrollShadowRight,
					Math.ceil(scrollLeft) < maxScrollLeft
				);
				this._scrollableHelper?.classList.toggle(Classes.ScrollShadowLeft, scrollLeft > 0);
			}
		};

		//For logged-in users only - placing a sticky component(in-page navigation) just below the toolbar and admin toolbar so that it can be visible.
		this._handleNavTopUsingDrupalToolbarsHeight = () => {
			const drupalToolbarsHeight = window
				.getComputedStyle(document.documentElement)
				.getPropertyValue('--drupal-displace-offset-top');
			if (drupalToolbarsHeight) {
				this._ctx.style.top = drupalToolbarsHeight;
			}
		};

		window.addEventListener('resize', () => {
			this._updateNavShadows();
			this._updateInPageNav();
			this._handleNavTopUsingDrupalToolbarsHeight();
		});

		this._inPageNav.addEventListener('scroll', () => {
			this._updateNavShadows();
		});

		window.addEventListener('scroll', () => {
			this._updateNavShadows();
			this._updateInPageNav();
		});

		window.addEventListener('load', () => {
			this._createAllAnchors();
			this._updateNavShadows();
			this._updateInPageNav();
			this._handleNavTopUsingDrupalToolbarsHeight();
		});
	}

	@EventListener('click', Selectors.ArrowLeftBtn)
	private _handleLeftArrowClick(event) {
		this._inPageNav.scroll({
			left: this._inPageNav.scrollLeft - this._getScrollStep(),
			behavior: 'smooth',
		});
	}

	@EventListener('click', Selectors.ArrowRightBtn)
	private _handleRightArrowClick(event) {
		this._inPageNav.scroll({
			left: this._inPageNav.scrollLeft + this._getScrollStep(),
			behavior: 'smooth',
		});
	}

	public _createAllAnchors() {
		if (this._allAnchorTriggers.length) {
			this._allAnchorTriggers.forEach((el) => {
				const singleAnchor: any = document.createElement('a');
				singleAnchor.setAttribute('href', `#${el.id}`);
				const singleAnchorText =
					el.dataset.inpagenavText && document.createTextNode(el?.dataset?.inpagenavText);
				singleAnchor?.appendChild(singleAnchorText);
				this._inPageNav?.appendChild(singleAnchor);
				this._inPageNav?.classList.add(States.InPageNavVisible);
			});
		}
	}

	public _getScrollStep() {
		const scrollWidth = this._inPageNav.scrollWidth;
		const scrollStep = scrollWidth / this._allAnchorTriggers.length;
		return scrollStep;
	}
}
